/**
 * Public Shared Reading page — /tarot/reading/:readingId
 * No auth. Renders reading + cards + sponsored widget + viral CTAs.
 * Beautiful when forwarded on WhatsApp (OG meta tags + readable content).
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Sparkles, MessageCircle, Share2 } from 'lucide-react';
import api from '../../utils/api';
import SponsoredTools from '../../components/SponsoredTools';

const SITE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const TarotPublicReading = () => {
  const { readingId } = useParams();
  const [search] = useSearchParams();
  const ref = search.get('ref') || '';
  const [reading, setReading] = useState(null);
  const [settings, setSettings] = useState({ whatsapp_cta_url: 'https://wa.me/919579369830?text=TAROT', whatsapp_cta_label: 'Need personal guidance? Chat on WhatsApp' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get('/anant-tarot/public-settings').then(r => setSettings(s => ({ ...s, ...r.data }))).catch(() => {});
    api.get(`/anant-tarot/public-reading/${readingId}`)
      .then(r => {
        const rd = r.data.reading;
        setReading(rd);
        // SEO + OG meta
        const qShort = (rd.question || '').slice(0, 60);
        const sharer = rd.sharer_name || 'A friend';
        const title = `${sharer}'s Anant Inner Self Reading — "${qShort}"`;
        const desc = (rd.reading_text || '').replace(/\*\*/g, '').slice(0, 200);
        document.title = title;
        const set = (sel, key, val) => {
          let tag = document.querySelector(sel);
          if (!tag) { tag = document.createElement('meta'); tag.setAttribute(key.split('=')[0], key.split('=')[1].replace(/"/g, '')); document.head.appendChild(tag); }
          tag.setAttribute('content', val);
        };
        const ensure = (k, v) => { let t = document.querySelector(`meta[name="${k}"]`); if (!t) { t = document.createElement('meta'); t.setAttribute('name', k); document.head.appendChild(t); } t.setAttribute('content', v); };
        const ensureOg = (p, v) => { let t = document.querySelector(`meta[property="${p}"]`); if (!t) { t = document.createElement('meta'); t.setAttribute('property', p); document.head.appendChild(t); } t.setAttribute('content', v); };
        ensure('description', desc);
        ensureOg('og:title', title);
        ensureOg('og:description', desc);
        ensureOg('og:type', 'article');
        ensureOg('og:url', window.location.href);
        const firstCardImg = `${SITE}/api/anant-tarot/og-image/${readingId}.png`;
        ensureOg('og:image', firstCardImg);
        ensureOg('og:image:width', '1200');
        ensureOg('og:image:height', '630');
        ensure('twitter:image', firstCardImg);
        ensure('twitter:card', 'summary_large_image');
      })
      .catch(e => setError(e.response?.data?.detail || 'Reading not found or not shared'))
      .finally(() => setLoading(false));
  }, [readingId]);

  if (loading) return <div className="min-h-screen pt-24 text-center text-slate-400 bg-slate-950">Loading…</div>;
  if (error || !reading) {
    return (
      <div className="min-h-screen pt-24 px-4 bg-slate-950 text-center text-white">
        <div className="text-amber-300 text-2xl font-bold mb-2">Reading not available</div>
        <p className="text-slate-400 text-sm max-w-md mx-auto">This reading may have been deleted or the sharer revoked public access.</p>
        <Link to="/anant-tarot" className="mt-4 inline-block px-5 py-2.5 rounded-xl bg-amber-500 text-black font-bold">Get Your Own Reading</Link>
      </div>
    );
  }

  // Parse the markdown-ish reading into sections
  const sections = (reading.reading_text || '').split(/\n\*\*/).map((s, i) => i === 0 ? s : `**${s}`);
  const renderSection = (txt) => txt.split('\n').map((line, idx) => {
    if (line.startsWith('**')) {
      const heading = line.replace(/\*\*/g, '');
      return <h3 key={idx} className="text-xl font-bold text-amber-200 mt-5 mb-2">{heading}</h3>;
    }
    if (!line.trim()) return null;
    return <p key={idx} className="text-slate-200 leading-relaxed mb-2">{line.replace(/\*\*/g, '')}</p>;
  });

  const inviteUrl = `${window.location.origin}/anant-tarot${ref ? `?ref=${ref}` : ''}`;
  // Use the backend OG endpoint for share URL (WhatsApp/Telegram/FB scraper gets the card image)
  const backendBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  const shareUrlForBots = `${backendBase}/api/anant-tarot/og/${readingId}${ref ? `?ref=${ref}` : ''}`;
  const shareText = `🔮 *${reading.sharer_name}'s Anant Inner Self Reading*\n\n_${reading.question}_\n\n${shareUrlForBots}\n\n✨ Draw your own 5-card spread: ${inviteUrl}`;
  const waShare = `https://wa.me/?text=${encodeURIComponent(shareText)}`;

  return (
    <div className="min-h-screen pt-20 pb-16 px-4 sm:px-6 bg-gradient-to-br from-slate-950 via-violet-950 to-slate-950 text-white" data-testid="public-reading-page">
      <div className="max-w-3xl mx-auto">
        {/* Breadcrumb */}
        <nav className="text-xs text-slate-400 mb-5 flex items-center gap-2 flex-wrap">
          <Link to="/" className="hover:text-amber-300">Home</Link>
          <span>/</span>
          <Link to="/tarot/gallery" className="hover:text-amber-300">Tarot</Link>
          <span>/</span>
          <span className="text-amber-300">Shared Reading</span>
        </nav>

        <div className="text-xs text-fuchsia-300 uppercase tracking-[0.3em] mb-1">Anant Inner Self Guidance</div>
        <h1 className="text-3xl sm:text-4xl font-extrabold bg-gradient-to-r from-fuchsia-300 via-violet-300 to-amber-300 bg-clip-text text-transparent mb-2" data-testid="public-reading-title">
          {reading.sharer_name}'s Reading
        </h1>
        <p className="text-sm sm:text-base text-fuchsia-100/80 italic">"{reading.question}"</p>

        {/* Cards row */}
        <div className="mt-6 grid grid-cols-5 gap-2 sm:gap-3">
          {(reading.cards || []).map((c, i) => (
            <Link key={i} to={`/tarot/card/${c.cardId ?? c.card_id ?? ''}`} className="block group" data-testid={`public-card-${i}`}>
              <div className="aspect-[2/3] rounded-lg overflow-hidden border border-amber-500/30 bg-slate-900">
                <img src={`${SITE}${c.image_url}`} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition"
                  style={{ filter: 'saturate(1.25) contrast(1.1) brightness(1.05) drop-shadow(0 0 6px rgba(255,200,80,0.25))' }} />
              </div>
              <div className="mt-1 text-[10px] sm:text-xs text-center text-amber-200 font-bold truncate">{c.name}</div>
            </Link>
          ))}
        </div>

        {/* Reading text */}
        <article className="mt-7 rounded-2xl border border-amber-500/20 bg-slate-900/60 p-5 sm:p-7" data-testid="public-reading-body">
          {sections.map((sec, i) => <div key={i}>{renderSection(sec)}</div>)}
        </article>

        {/* CTAs */}
        <div className="mt-8 rounded-2xl border-2 border-amber-400/50 bg-gradient-to-br from-amber-950 to-slate-900 p-5 sm:p-6 text-center">
          <Sparkles className="w-8 h-8 text-amber-300 mx-auto mb-2" />
          <h2 className="text-xl sm:text-2xl font-extrabold text-amber-200 mb-2">Move beyond stillness. Ask the Universe your own question.</h2>
          <p className="text-sm text-amber-100/80 mb-4">Free 1-card reading + premium 5-card spreads in 10 Indian languages.</p>
          <div className="flex gap-2 justify-center flex-wrap">
            <Link to={inviteUrl.replace(window.location.origin, '')} data-testid="public-cta-reading"
              className="px-5 py-3 rounded-xl bg-amber-500 text-black font-bold inline-flex items-center gap-2 hover:bg-amber-400">
              <Sparkles className="w-4 h-4" /> Draw Your 5-Card Spread
            </Link>
            <a href={waShare} target="_blank" rel="noreferrer" data-testid="public-cta-share"
              className="px-5 py-3 rounded-xl bg-green-600 text-white font-semibold inline-flex items-center gap-2 hover:bg-green-500">
              <MessageCircle className="w-4 h-4" /> Forward on WhatsApp
            </a>
          </div>
        </div>

        {/* Sponsored — peak intent moment */}
        <SponsoredTools context="public-reading" maxItems={6} />

        {/* WhatsApp support CTA — admin-editable */}
        <a
          href={settings.whatsapp_cta_url}
          target="_blank" rel="noreferrer"
          data-testid="tarot-whatsapp-cta"
          className="mt-6 flex items-center justify-center gap-3 rounded-2xl border-2 border-green-500/50 bg-gradient-to-r from-green-600/25 to-emerald-600/25 hover:from-green-500/30 hover:to-emerald-500/30 transition p-4 sm:p-5 group"
        >
          <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-green-500 flex items-center justify-center group-hover:scale-110 transition">
            <MessageCircle className="w-6 h-6 text-white" />
          </div>
          <div className="text-left">
            <div className="text-[10px] font-bold uppercase tracking-wider text-green-300">Personal Guidance</div>
            <div className="text-base sm:text-lg font-extrabold text-green-100">{settings.whatsapp_cta_label}</div>
          </div>
        </a>

        <div className="mt-10 text-center text-xs text-slate-500">
          <Link to="/tarot/gallery" className="text-amber-300 underline">Browse all 79 Tarot cards</Link>
        </div>
      </div>
    </div>
  );
};

export default TarotPublicReading;
